import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types ── */

export interface Company {
  id: string;
  name: string;
  tenantId: string;
  createdAt: string;
}

export interface CompanyInput {
  name: string;
  tenantId: string;
}

export interface Branch {
  id: string;
  name: string;
  companyId: string;
  createdAt: string;
}

export interface BranchInput {
  name: string;
  companyId: string;
}

/** Espelha `NumberSequenceResponseDto`. */
export interface NumberSequence {
  id: string;
  tenantId: string;
  companyId: string;
  branchId?: string;
  entity: string;
  format: string;
  currentSequence: number;
  startNumber: number;
  endNumber?: number;
  prefix?: string;
  suffix?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NumberSequenceInput {
  companyId: string;
  entity: string;
  format: string;
  startNumber?: number;
  prefix?: string;
  suffix?: string;
}

export interface GenerateNumberResult {
  number: string;
}

/* ── Tenant ── */

export interface TenantData {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
}

export interface OrganizationStats {
  companiesCount: number;
  branchesCount: number;
  numberSequencesCount: number;
}

export function useCurrentTenant(tenantId: string) {
  return useQuery({
    queryKey: queryKeys.tenant.current(tenantId),
    queryFn: () => apiClient.get<TenantData>(`/tenants/${tenantId}`),
    enabled: tenantId.length > 0,
  });
}

export function useOrganizationStats(tenantId: string) {
  return useQuery({
    queryKey: [...queryKeys.organization.companies(), 'stats', tenantId],
    // Não existe GET /tenants/:id/stats no backend — compõe de endpoints reais.
    queryFn: async (): Promise<OrganizationStats> => {
      const companies = await apiClient.get<Company[]>(`/tenants/${tenantId}/companies`);
      const perCompany = await Promise.all(
        companies.map(async (company) => {
          const [branches, sequences] = await Promise.all([
            apiClient.get<unknown[]>(`/companies/${company.id}/branches`).catch(() => []),
            apiClient.get<unknown[]>(`/companies/${company.id}/number-sequences`).catch(() => []),
          ]);
          return { branches: branches.length, sequences: sequences.length };
        }),
      );
      return {
        companiesCount: companies.length,
        branchesCount: perCompany.reduce((sum, c) => sum + c.branches, 0),
        numberSequencesCount: perCompany.reduce((sum, c) => sum + c.sequences, 0),
      };
    },
    enabled: tenantId.length > 0,
  });
}

/* ── Companies ── */

export function useCompanies(tenantId: string) {
  return useQuery({
    queryKey: [...queryKeys.organization.companies(), tenantId],
    queryFn: () =>
      apiClient.get<Company[]>(`/tenants/${tenantId}/companies`),
    enabled: tenantId.length > 0,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyInput) =>
      apiClient.post<Company>('/companies', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.organization.companies() }),
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: CompanyInput & { id: string }) =>
      apiClient.patch<Company>(`/companies/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.organization.companies() }),
  });
}

/* ── Branches ── */

export function useBranches(companyId: string) {
  return useQuery({
    queryKey: [...queryKeys.organization.branches(), companyId],
    queryFn: () =>
      apiClient.get<Branch[]>(`/companies/${companyId}/branches`),
    enabled: companyId.length > 0,
  });
}

export function useCreateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BranchInput) =>
      apiClient.post<Branch>('/branches', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.organization.branches() }),
  });
}

export function useUpdateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: BranchInput & { id: string }) =>
      apiClient.patch<Branch>(`/branches/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.organization.branches() }),
  });
}

/* ── Number Sequences ── */

export function useNumberSequences(companyId: string) {
  return useQuery({
    queryKey: [...queryKeys.organization.numberSequences(), companyId],
    queryFn: () =>
      apiClient.get<NumberSequence[]>(`/companies/${companyId}/number-sequences`),
    enabled: companyId.length > 0,
  });
}

export function useCreateNumberSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NumberSequenceInput) =>
      apiClient.post<NumberSequence>('/number-sequences', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.organization.numberSequences() }),
  });
}

export function useGenerateNumber() {
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<GenerateNumberResult>(`/number-sequences/${id}/generate`),
  });
}
